import { useCallback, useEffect, useState } from 'react'
import { cacheWeeklyIssue, getLatestCachedWeeklyIssue, initializeLocalDataCompatibility } from '../data/db'
import type { WeeklyIssue } from '../types/daily'

interface WeeklyIssueLoadState {
  issue?: WeeklyIssue
  loading: boolean
  source?: 'network' | 'cache'
  warning?: string
  unavailable?: boolean
}

export function useWeeklyIssue() {
  const [state, setState] = useState<WeeklyIssueLoadState>({ loading: true })

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: !current.issue }))
    try {
      await initializeLocalDataCompatibility()
      const response = await fetch('/data/weekly/latest.json', { cache: 'no-store' })
      if (response.status === 404 || response.headers.get('content-type')?.includes('text/html')) {
        const cached = await getLatestCachedWeeklyIssue()
        setState(cached
          ? { issue: cached, loading: false, source: 'cache', warning: '最新周刊尚未发布，正在显示上次保存的周刊。' }
          : { loading: false, unavailable: true })
        return
      }
      if (!response.ok) throw new Error(`request failed (${response.status})`)
      const candidate = (await response.json()) as unknown
      const { validateWeeklyIssue } = await import('../data/validateWeeklyIssue')
      const result = validateWeeklyIssue(candidate)
      if (!result.valid || !result.data) throw new Error(`invalid weekly issue: ${result.errors[0] ?? 'unknown error'}`)
      await cacheWeeklyIssue(result.data)
      setState({ issue: result.data, loading: false, source: 'network' })
    } catch (error) {
      const cached = await getLatestCachedWeeklyIssue()
      console.error('Weekly issue load failed', error)
      setState(cached
        ? { issue: cached, loading: false, source: 'cache', warning: '网络暂时不可用，正在显示上次保存的周刊。' }
        : { loading: false, warning: '暂时无法获取周刊，请检查网络后重试。' })
    }
  }, [])

  useEffect(() => { void load() }, [load])
  return { ...state, refresh: load }
}
